import {
  AdminDatePickerInputModal,
  Button,
  Div,
  ErrorToast,
  H5,
  Modal,
  PropsModal,
} from '@vgrubs/components';
import { HolidayHoursReponseV2 } from 'components/src/types/location.types';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import styled from 'styled-components';
import {
  dateTimeInitial,
  format24hToDate,
  formatDateToTime24h,
} from '../../helpers/time-helpers';

export const AddHolidayHours = ({
  modalIsOpen,
  closeModal,
  setHolidayHours,
}: PropsModal & {
  modalIsOpen:
    | boolean
    | { edit: null | HolidayHoursReponseV2; index?: number }
    | null;
  setHolidayHours: Dispatch<SetStateAction<HolidayHoursReponseV2[]>>;
}) => {
  const [date, setDate] = useState<Date[]>([]);
  const [timeFrom, setTimeFrom] = useState<Date[]>([]);
  const [timeTo, setTimeTo] = useState<Date[]>([]);

  const holidayHourData = modalIsOpen;

  useEffect(() => {
    if (typeof holidayHourData === 'object' && holidayHourData?.edit) {
      const chosenDate = DateTime.fromISO(holidayHourData.edit.date).toJSDate();

      setDate([chosenDate]);
      setTimeFrom([format24hToDate(holidayHourData.edit.startTime)]);
      setTimeTo([format24hToDate(holidayHourData.edit.endTime)]);
    } else {
      setDate([]);
      setTimeFrom([dateTimeInitial]);
      setTimeTo([dateTimeInitial]);
    }
  }, [holidayHourData]);

  async function handleAdd() {
    if (!date.length || !timeFrom || !timeTo)
      return ErrorToast('Please fill in all information');

    if (timeFrom[0] >= timeTo[0]) {
      return ErrorToast('From time must be before to time');
    }

    const chosenDate = DateTime.fromJSDate(date[0]).toISODate();

    const body = {
      date: chosenDate,
      startTime: formatDateToTime24h(timeFrom[0]),
      endTime: formatDateToTime24h(timeTo[0]),
    };

    if (typeof holidayHourData === 'object' && holidayHourData?.edit) {
      setHolidayHours(
        (prev) =>
          prev.map((h, i) =>
            i === holidayHourData.index ? body : h
          ) as HolidayHoursReponseV2[]
      );
    } else {
      setHolidayHours((prev) => [...prev, body] as HolidayHoursReponseV2[]);
    }

    closeModal?.();
    setDate([]);
    setTimeFrom([dateTimeInitial]);
    setTimeTo([dateTimeInitial]);
  }

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`${
        typeof holidayHourData === 'object' && holidayHourData?.edit?.date
          ? 'Edit'
          : 'Add'
      } Day`}
      textAlign="left"
      width=""
    >
      <H5>Select Day</H5>
      <Div $flex $justifyCenter>
        <AdminDatePickerWrapperStyled>
          <AdminDatePickerStyled
            value={date}
            onChange={(date) => setDate(date)}
            options={{
              dateFormat: 'M d, Y',
              inline: true,
              minDate: 'today',
              maxDate: 'Jan 1, 2100',
            }}
            className="form-control"
          />
        </AdminDatePickerWrapperStyled>
      </Div>

      <H5 $mt="32" $mb="16" $center>
        Select Hours From - To
      </H5>
      <Div $flex $gap="24px">
        <AdminDatePickerInputModal
          format="hh:mm a"
          options={{
            enableTime: true,
            dateFormat: 'Y-m-d H:i',
            noCalendar: true,
          }}
          placeholder="From"
          $textCenter
          onChange={setTimeFrom}
          value={timeFrom}
        />
        <AdminDatePickerInputModal
          format="hh:mm a"
          options={{
            enableTime: true,
            dateFormat: 'Y-m-d H:i',
            noCalendar: true,
          }}
          placeholder="To"
          $textCenter
          onChange={setTimeTo}
          value={timeTo}
        />
      </Div>

      <Button $selected $w="100%" $mt="32" onClick={() => handleAdd()}>
        Confirm
      </Button>
    </Modal>
  );
};

const AdminDatePickerWrapperStyled = styled.div`
  position: relative;
  gap: 16px;
  font-size: 15px;
  line-height: 18px;
  color: ${({ theme }) => theme.darkGray};
  background-color: ${({ theme }) => theme.white};
  display: inline-block;
  padding: 12px 12px 16px 12px;
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  border-radius: 20px;
  background-color: ${({ theme }) => theme.white};

  .flatpickr-calendar.rangeMode.animate.inline {
    margin: 0 !important;
  }

  .flatpickr-day.inRange {
    background: #f7f7f7;
    border-color: #f7f7f7;
    border-radius: 50%;
    box-shadow: none;
  }

  .flatpickr-day.selected,
  .flatpickr-day.startRange,
  .flatpickr-day.endRange,
  .flatpickr-day.selected.inRange,
  .flatpickr-day.startRange.inRange,
  .flatpickr-day.endRange.inRange,
  .flatpickr-day.selected:focus,
  .flatpickr-day.startRange:focus,
  .flatpickr-day.endRange:focus,
  .flatpickr-day.selected:hover,
  .flatpickr-day.startRange:hover,
  .flatpickr-day.endRange:hover,
  .flatpickr-day.selected.prevMonthDay,
  .flatpickr-day.startRange.prevMonthDay,
  .flatpickr-day.endRange.prevMonthDay,
  .flatpickr-day.selected.nextMonthDay,
  .flatpickr-day.startRange.nextMonthDay,
  .flatpickr-day.endRange.nextMonthDay {
    background: #e61e29;
    border-color: #e61e29;
    color: #fff;
    border-radius: 50%;
    box-shadow: none !important;
  }

  .flatpickr-day.selected.endRange,
  .flatpickr-day.startRange.endRange,
  .flatpickr-day.endRange.endRange,
  .flatpickr-day.selected.startRange,
  .flatpickr-day.startRange.startRange {
    border-radius: 50%;
  }

  .flatpickr-day.today {
    border-color: #e61e29;
  }

  .flatpickr-calendar {
    border: none;
    border: none;
    box-shadow: none;
  }

  .flatpickr-weekday {
    font-weight: normal !important;
  }

  .flatpickr-weekdays {
    height: 50px;
  }

  .flatpickr-input {
    display: none;
    pointer-events: none;
  }

  .flatpickr-current-month .flatpickr-monthDropdown-months {
    -moz-appearance: none !important; /* Firefox */
    -webkit-appearance: none !important; /* Safari and Chrome */
    appearance: none !important;
  }

  .flatpickr-current-month .flatpickr-monthDropdown-months:hover {
    background: none;
    color: #e61e29;
  }
`;

const AdminDatePickerStyled = styled(Flatpickr)`
  width: 250px;
  border: none;
  border-radius: 200px;
  font-size: 15px;
  line-height: 18px;
  padding: 12.5px 24px 12.5px 16px;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;

  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  cursor: pointer;

  :focus {
    outline: none !important;
    /* border: 1px solid ${({ theme }) => theme.primary}; */
  }
`;
